import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { EvidenceAttentionItem } from "../../types/evidence";

type AskSeverity = EvidenceAttentionItem["severity"];

const ASK_COLOR: Record<AskSeverity, string> = { required: "error.main", recommended: "warning.main" };

export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    // Text and links share a baseline; the help mark has none, so it centres on the row.
    <Typography
      variant="overline"
      sx={{
        display: "flex", alignItems: "baseline", gap: 0.5, minHeight: 20,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", lineHeight: "20px", color: "#667085",
        "& > .MuiIconButton-root": { alignSelf: "center" },
      }}
    >
      {children}
      {action}
    </Typography>
  );
}

// Something that needs the agent is said by the colour of its words, nothing else: no dots, no chips.
export function AskText({ severity, children }: { severity: AskSeverity | null; children: ReactNode }) {
  return (
    <Box component="span" data-severity={severity ?? undefined} sx={{ color: severity ? ASK_COLOR[severity] : undefined }}>
      {children}
    </Box>
  );
}

export default function ChecklistRow({ leading, primary, secondary, action, children }: {
  leading?: ReactNode;
  primary: ReactNode;
  secondary?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Box component="li" sx={{ mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {leading}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{primary}</Typography>
          {secondary && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {secondary}
            </Typography>
          )}
        </Box>
        {/* The row's text can wrap; without this the flex row squeezes the action to its minimum
            width and the buttons stop lining up down the column. */}
        {action && <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>{action}</Box>}
      </Box>
      {children}
    </Box>
  );
}
